import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, useSlots, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { inputProps, inputEmits } from './input';
import { UPDATE_MODEL_VALUE } from "../../constants";
import { UiIconClose, UiIconDeleteOutline } from '../../icons';
import createComponentId from '../../utils/createComponentId';
import { Loading } from '../../directives';


const __default__ = {
    name: 'UiInput',
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: inputProps,
  emits: inputEmits,
  setup(__props, { expose, emit: emits }) {

const props = __props



const slots = useSlots();

const inputRef = ref<HTMLInputElement>();
const prependRef = ref<HTMLElement>();
const bodyRef = ref<HTMLElement>();
const innerRef = ref<HTMLElement>();
const inputId = createComponentId('ui-input');
const isFocused = ref(false);
const multipleInputText = ref('');

const prependWidth = ref(0);
const inputLeft = ref(0);
const bodyLeft = ref(0);

const measureLayout = (): void => {
    prependWidth.value = prependRef.value?.offsetWidth ?? 0;
    inputLeft.value = inputRef.value?.offsetLeft ?? 0;
    bodyLeft.value = bodyRef.value?.offsetLeft ?? 0;
};

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
    measureLayout();
    if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => measureLayout());
        if (innerRef.value) resizeObserver.observe(innerRef.value);
        if (prependRef.value) resizeObserver.observe(prependRef.value);
    }
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    resizeObserver = null;
});

watch(() => Boolean(slots.prepend), () => {
    nextTick(() => {
        measureLayout();
        if (resizeObserver && prependRef.value) resizeObserver.observe(prependRef.value);
    });
});

const multipleValue = computed<string[]>(() =>
    Array.isArray(props.modelValue) ? props.modelValue : [],
);

const visibleChips = computed<string[]>(() =>
    props.max ? multipleValue.value.slice(0, props.max) : multipleValue.value,
);

const labelPositionStyle = computed<Record<string, string>>(() => {
    const hasPrepend = prependWidth.value > 0;
    const leftRestingPx = inputLeft.value || (hasPrepend ? prependWidth.value : 0);

    if (props.multiple) {
        const isFloating = isFocused.value || multipleValue.value.length > 0 || Boolean(multipleInputText.value.trim());
        if (isFloating) return { top: "-18px", left: "9px", height: "18px", transform: "none" };
        return { top: "0", left: `${bodyLeft.value}px`, height: "var(--_min-height)" };
    }

    const text = Array.isArray(props.modelValue) ? props.modelValue.join("") : (props.modelValue ?? "");
    const isFloating = isFocused.value || Boolean(text.trim());
    const leftPx = isFloating ? 0 : leftRestingPx;

    if (hasPrepend) {
        if (isFloating) return { top: "-90%", left: "9px" };
        return { top: "0", left: `${leftRestingPx}px` };
    }

    if (props.view === "border-bottom") {
        if (isFloating) return { top: "-90%", left: "0" };
        return { top: "50%", left: "0", transform: "translateY(-50%)" };
    }

    if (isFloating) return { top: "-90%", left: "9px" };
    return { top: "50%", left: `${leftPx}px`, transform: "translateY(-50%)" };
});

const modifiers = computed<string[]>(() => {
    return [
        'ui-input',
        props.size && `--${props.size}`,
        props.disabled && `--disabled`,
        props.readonly && `--readonly`,
        props.loading && `--loading`,
        props.view && `--${props.view}`,
        props.multiple && `--multiple`,
    ];
});

const isInteractive = (): boolean => !props.disabled && !props.readonly && !props.loading;

const isFocusable = (): boolean => !props.disabled && !props.loading;

const prepareInputValue = (event: Event): string => (event.target as HTMLInputElement).value;

const addTag = (): void => {
    if (!isInteractive()) return;
    const text = multipleInputText.value.trim();
    if (!text) return;
    if (!multipleValue.value.includes(text)) {
        emits(UPDATE_MODEL_VALUE, [...multipleValue.value, text]);
    }
    multipleInputText.value = '';
    if (inputRef.value) inputRef.value.value = '';
};

const removeTag = (index: number): void => {
    emits(UPDATE_MODEL_VALUE, multipleValue.value.filter((_, i) => i !== index));
};

const handleInput = (event: Event): void => {
    if (!isInteractive()) return;
    if (props.multiple) {
        multipleInputText.value = prepareInputValue(event);
    } else {
        emits(UPDATE_MODEL_VALUE, prepareInputValue(event));
        emits('input', prepareInputValue(event));
    }
};

const handleFocus = (event: Event): void => {
    if (!isFocusable()) return;
    isFocused.value = true;
    emits('focus', prepareInputValue(event));
};

const handleBlur = (event: Event): void => {
    if (!isFocusable()) return;
    if (props.multiple) addTag();
    isFocused.value = false;
    emits('blur', prepareInputValue(event));
};

const handleChange = (event: Event): void => {
    if (!isInteractive() || props.multiple) return;
    emits('change', prepareInputValue(event));
};

const handleKeydown = (event: KeyboardEvent): void => {
    if (event.code === "Enter") {
        event.preventDefault();
        if (props.addTrigger === "Enter") addTag();
    } else if (event.code === "Space" && props.addTrigger === "Space") {
        event.preventDefault();
        addTag();
    }
};

const handleClear = (): void => {
    if (props.disabled || props.loading) return;
    const empty = props.multiple ? [] : '';
    emits(UPDATE_MODEL_VALUE, empty);
    emits('input', '');
    emits('change', '');
    emits('clear', empty);
};

expose({ inputRef });

return { __sfc: true,props, emits, slots, inputRef, prependRef, bodyRef, innerRef, inputId, isFocused, multipleInputText, prependWidth, inputLeft, bodyLeft, measureLayout, resizeObserver, multipleValue, visibleChips, labelPositionStyle, modifiers, isInteractive, isFocusable, prepareInputValue, addTag, removeTag, handleInput, handleFocus, handleBlur, handleChange, handleKeydown, handleClear, UiIconClose, UiIconDeleteOutline }
}

})